/** Operator action dialogs (spec §8): create / close / cancel / confirm / merge
 *  / split. All are DISPATCHER-gated at the call site (the server is the real
 *  gate); viewers never see these controls.
 *
 *  Every mutating dialog threads `expected_updated_at` (the `updated_at` last
 *  read on the detail) for optimistic concurrency and surfaces a 409 stale write
 *  as a "refresh & retry" message via {@link isConflictError} — it does NOT
 *  invent a retry with a fresh timestamp (that would silently clobber a
 *  concurrent edit). */

import { useState } from 'react'
import { isConflictError } from '../../api/client.ts'
import { friendlyError } from '../../utils/friendlyError.ts'
import {
  useAffectedMeters,
  useCancelOutage,
  useCloseOutage,
  useConfirmOutage,
  useCreateOutage,
  useMergeOutages,
  useOutages,
  usePatchOutage,
  useSplitOutage,
} from '../../hooks/useOms.ts'
import type {
  OutageDetail,
  OutagePriority,
  OutageScope,
  ReportSource,
} from '../../types/oms.ts'
import { OUTAGE_PRIORITIES, PRIORITY_LABELS, REPORT_SOURCE_LABELS } from '../../types/oms.ts'
import { CausePicker } from './CausePicker.tsx'
import { DevicePicker, type SelectedDevice } from './DevicePicker.tsx'
import { MeterChecklist } from './MeterChecklist.tsx'
import { OmsDialog } from './OmsDialog.tsx'
import { deviceLabel, toDatetimeLocal } from './omsFormat.tsx'

// =========================================================================
// Shared error helper — 409 gets the refresh-&-retry message
// =========================================================================

function errorMessage(err: unknown): string {
  if (isConflictError(err)) {
    return 'This outage changed since you opened it (stale write). Refresh the outage and re-apply your action.'
  }
  return friendlyError(err, 'apply this action')
}

function ErrorNote({ error }: { error: unknown }) {
  if (!error) return null
  const conflict = isConflictError(error)
  return (
    <div
      className={`text-sm rounded-md px-3 py-2 border ${
        conflict
          ? 'text-amber-800 bg-amber-50 border-amber-200'
          : 'text-red-700 bg-red-50 border-red-200'
      }`}
    >
      {errorMessage(error)}
    </div>
  )
}

function PrimaryButton({
  onClick,
  disabled,
  children,
  variant = 'blue',
}: {
  onClick: () => void
  disabled?: boolean
  children: React.ReactNode
  variant?: 'blue' | 'red' | 'green' | 'gray'
}) {
  const colors: Record<string, string> = {
    blue: 'bg-blue-600 hover:bg-blue-700',
    red: 'bg-red-600 hover:bg-red-700',
    green: 'bg-emerald-600 hover:bg-emerald-700',
    gray: 'bg-gray-600 hover:bg-gray-700',
  }
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`text-sm px-3 py-1.5 rounded-md text-white disabled:opacity-50 ${colors[variant]}`}
    >
      {children}
    </button>
  )
}

function CancelButton({ onClick }: { onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className="text-sm px-3 py-1.5 rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50"
    >
      Cancel
    </button>
  )
}

// =========================================================================
// Create outage (manual)
// =========================================================================

const SCOPES: OutageScope[] = ['meter', 'transformer', 'feeder', 'substation']

export function CreateOutageDialog({
  onClose,
  onCreated,
}: {
  onClose: () => void
  onCreated?: (detail: OutageDetail) => void
}) {
  const [scope, setScope] = useState<OutageScope>('transformer')
  // Resolved via the scoped DevicePicker — dispatch searches by name/number,
  // never types a raw internal id (review finding K).
  const [device, setDevice] = useState<SelectedDevice | null>(null)
  // null = every meter on the transformer
  const [meterIds, setMeterIds] = useState<number[] | null>(null)
  const [source, setSource] = useState<ReportSource>('call_in')
  const [startAt, setStartAt] = useState(() => toDatetimeLocal(new Date().toISOString()))
  const [restored, setRestored] = useState(false)
  const [endAt, setEndAt] = useState('')
  const [confirmed, setConfirmed] = useState(false)
  const [causeDetail, setCauseDetail] = useState<string | null>(null)
  const [causeDescription, setCauseDescription] = useState('')
  const [priority, setPriority] = useState<OutagePriority | ''>('')
  const [assignedCrew, setAssignedCrew] = useState('')
  const [notes, setNotes] = useState('')

  const mutation = useCreateOutage()
  const startIso = startAt ? new Date(startAt).toISOString() : null
  const endIso = restored && endAt ? new Date(endAt).toISOString() : null
  const timeProblem =
    !startIso
      ? 'Enter when it started.'
      : restored && !endIso
        ? 'Enter when power came back.'
        : endIso && startIso && endIso <= startIso
          ? 'Power back must be after the start.'
          : null
  const valid = device !== null && meterIds?.length !== 0 && timeProblem === null

  function submit() {
    if (device === null || !valid) return
    mutation.mutate(
      {
        scope,
        device_id: device.id,
        start_time: startIso,
        end_time: endIso,
        meter_ids: scope === 'transformer' ? meterIds : null,
        report_source: source,
        confirmed,
        cause_detail: causeDetail,
        cause_description: causeDescription || null,
        priority: restored ? null : priority || null,
        assigned_crew: assignedCrew || null,
        notes: notes || null,
      },
      {
        onSuccess: detail => {
          onCreated?.(detail)
          onClose()
        },
      },
    )
  }

  return (
    <OmsDialog
      title="Report an outage"
      onClose={onClose}
      footer={
        <>
          <CancelButton onClick={onClose} />
          <PrimaryButton onClick={submit} disabled={!valid || mutation.isPending}>
            {mutation.isPending ? 'Saving…' : restored ? 'Record outage' : 'Create outage'}
          </PrimaryButton>
        </>
      }
    >
      <ErrorNote error={mutation.error} />
      <p className="text-gray-600">
        For outages the meters didn’t report: a customer call-in, a crew report or
        SCADA. Use “already restored” to record one after the fact.
      </p>
      <div className="grid grid-cols-2 gap-3">
        <label className="block">
          <span className="text-xs text-gray-500">How was it reported?</span>
          <select
            value={source}
            onChange={e => setSource(e.target.value as ReportSource)}
            className="mt-1 w-full text-sm border border-gray-300 rounded-md px-2 py-1.5"
          >
            {(Object.keys(REPORT_SOURCE_LABELS) as ReportSource[]).map(k => (
              <option key={k} value={k}>
                {REPORT_SOURCE_LABELS[k]}
              </option>
            ))}
          </select>
        </label>
        <label className="block">
          <span className="text-xs text-gray-500">Scope</span>
          <select
            value={scope}
            onChange={e => {
              setScope(e.target.value as OutageScope)
              // A selection from the previous scope is meaningless — clear it.
              setDevice(null)
              setMeterIds(null)
            }}
            className="mt-1 w-full text-sm border border-gray-300 rounded-md px-2 py-1.5"
          >
            {SCOPES.map(s => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </label>
      </div>
      <div className="block">
        <span className="text-xs text-gray-500">
          {scope === 'transformer' ? 'Transformer (search by number or address)' : `Device (${scope})`}
        </span>
        <DevicePicker
          scope={scope}
          value={device}
          onChange={d => {
            setDevice(d)
            setMeterIds(null)
          }}
        />
      </div>
      {scope === 'transformer' && device && (
        <div className="block">
          <span className="text-xs text-gray-500">
            Which meters were out? (uncheck the ones that kept power)
          </span>
          <MeterChecklist transformerId={device.id} value={meterIds} onChange={setMeterIds} />
          {meterIds?.length === 0 && (
            <div className="text-xs text-amber-700 mt-1">Check at least one meter.</div>
          )}
        </div>
      )}
      <div className="grid grid-cols-2 gap-3">
        <label className="block">
          <span className="text-xs text-gray-500">Out since</span>
          <input
            type="datetime-local"
            value={startAt}
            onChange={e => setStartAt(e.target.value)}
            className="mt-1 w-full text-sm border border-gray-300 rounded-md px-2 py-1.5"
          />
        </label>
        <div className="block">
          <label className="flex items-center gap-2 text-xs text-gray-500">
            <input
              type="checkbox"
              checked={restored}
              onChange={e => {
                setRestored(e.target.checked)
                // an after-the-fact record is normally a confirmed outage
                setConfirmed(e.target.checked)
              }}
            />
            Already restored at
          </label>
          <input
            type="datetime-local"
            value={endAt}
            onChange={e => setEndAt(e.target.value)}
            disabled={!restored}
            aria-label="Power back at"
            className="mt-1 w-full text-sm border border-gray-300 rounded-md px-2 py-1.5 disabled:bg-gray-50"
          />
        </div>
      </div>
      {timeProblem && device && <div className="text-xs text-amber-700">{timeProblem}</div>}
      <div className="grid grid-cols-2 gap-3">
        <label className="block">
          <span className="text-xs text-gray-500">Cause</span>
          <CausePicker value={causeDetail} onChange={setCauseDetail} />
        </label>
        {restored ? (
          <label className="block">
            <span className="text-xs text-gray-500">Assigned crew</span>
            <input
              value={assignedCrew}
              onChange={e => setAssignedCrew(e.target.value)}
              className="mt-1 w-full text-sm border border-gray-300 rounded-md px-2 py-1.5"
            />
          </label>
        ) : (
          <label className="block">
            <span className="text-xs text-gray-500">Priority</span>
            <select
              value={priority}
              onChange={e => setPriority(e.target.value as OutagePriority | '')}
              className="mt-1 w-full text-sm border border-gray-300 rounded-md px-2 py-1.5"
            >
              <option value="">-- none --</option>
              {OUTAGE_PRIORITIES.map(pr => (
                <option key={pr} value={pr}>
                  {PRIORITY_LABELS[pr]}
                </option>
              ))}
            </select>
          </label>
        )}
      </div>
      <label className="block">
        <span className="text-xs text-gray-500">Cause description</span>
        <input
          value={causeDescription}
          onChange={e => setCauseDescription(e.target.value)}
          className="mt-1 w-full text-sm border border-gray-300 rounded-md px-2 py-1.5"
        />
      </label>
      {!restored && (
        <label className="block">
          <span className="text-xs text-gray-500">Assigned crew</span>
          <input
            value={assignedCrew}
            onChange={e => setAssignedCrew(e.target.value)}
            className="mt-1 w-full text-sm border border-gray-300 rounded-md px-2 py-1.5"
          />
        </label>
      )}
      <label className="block">
        <span className="text-xs text-gray-500">Notes</span>
        <textarea
          value={notes}
          onChange={e => setNotes(e.target.value)}
          rows={2}
          className="mt-1 w-full text-sm border border-gray-300 rounded-md px-2 py-1.5"
        />
      </label>
      <label className="flex items-center gap-2 text-sm text-gray-700">
        <input type="checkbox" checked={confirmed} onChange={e => setConfirmed(e.target.checked)} />
        Confirmed real outage — counts toward reliability (SAIDI/SAIFI)
      </label>
    </OmsDialog>
  )
}

// =========================================================================
// State-change dialogs (close / cancel / confirm) — share a shape
// =========================================================================

type StateAction = 'close' | 'cancel' | 'confirm'

const STATE_META: Record<
  StateAction,
  { title: string; verb: string; variant: 'red' | 'green' | 'gray'; blurb: string }
> = {
  close: {
    title: 'Close outage',
    verb: 'Close outage',
    variant: 'gray',
    blurb: 'Mark this outage restored and closed. Use when power is confirmed back.',
  },
  cancel: {
    title: 'Cancel outage (false positive)',
    verb: 'Cancel as false positive',
    variant: 'red',
    blurb:
      'Cancel this event as a false positive. It will be excluded from reliability metrics.',
  },
  confirm: {
    title: 'Confirm outage',
    verb: 'Confirm outage',
    variant: 'green',
    blurb:
      'Confirm this is a real outage → it becomes reportable for reliability (SAIDI/SAIFI).',
  },
}

export function OutageStateDialog({
  action,
  outage,
  onClose,
  onDone,
}: {
  action: StateAction
  outage: OutageDetail
  onClose: () => void
  onDone?: (detail: OutageDetail) => void
}) {
  const [notes, setNotes] = useState('')
  const meta = STATE_META[action]

  const closeM = useCloseOutage()
  const cancelM = useCancelOutage()
  const confirmM = useConfirmOutage()
  const mutation =
    action === 'close' ? closeM : action === 'cancel' ? cancelM : confirmM

  function submit() {
    const body = {
      expected_updated_at: outage.updated_at,
      notes: notes || null,
    }
    mutation.mutate(
      { id: outage.id, body },
      {
        onSuccess: detail => {
          onDone?.(detail)
          onClose()
        },
      },
    )
  }

  return (
    <OmsDialog
      title={meta.title}
      onClose={onClose}
      footer={
        <>
          <CancelButton onClick={onClose} />
          <PrimaryButton
            onClick={submit}
            disabled={mutation.isPending}
            variant={meta.variant}
          >
            {mutation.isPending ? 'Working…' : meta.verb}
          </PrimaryButton>
        </>
      }
    >
      <ErrorNote error={mutation.error} />
      <p className="text-gray-600">{meta.blurb}</p>
      <div className="text-xs text-gray-500">
        Outage #{outage.id} ·{' '}
        {deviceLabel(outage.scope, outage.device_id, outage.unresolved_meter_number)}
      </div>
      <label className="block">
        <span className="text-xs text-gray-500">Notes (optional)</span>
        <textarea
          value={notes}
          onChange={e => setNotes(e.target.value)}
          rows={3}
          className="mt-1 w-full text-sm border border-gray-300 rounded-md px-2 py-1.5"
        />
      </label>
    </OmsDialog>
  )
}

// =========================================================================
// Merge — roll up other event IDs under this primary
// =========================================================================

export function MergeOutageDialog({
  primary,
  onClose,
  onDone,
}: {
  primary: OutageDetail
  onClose: () => void
  onDone?: (detail: OutageDetail) => void
}) {
  const [selected, setSelected] = useState<Set<number>>(new Set())
  const [notes, setNotes] = useState('')
  const mutation = useMergeOutages()
  // Candidate events to roll up: other ACTIVE outages — never the primary
  // itself, never its parent (the ancestor the merge cycle guard rejects with
  // a 400), and no longer events ALREADY parented under the primary
  // (parent_event_id is on OutageListItem as of the rollup-visibility work).
  const { data, isLoading } = useOutages({ status: 'active', limit: 200 })
  const candidates = (data?.items ?? []).filter(
    o =>
      o.id !== primary.id &&
      o.id !== primary.parent_event_id &&
      o.parent_event_id !== primary.id,
  )

  const mergeIds = [...selected]
  const valid = mergeIds.length > 0

  function toggle(id: number) {
    setSelected(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  function submit() {
    if (!valid) return
    mutation.mutate(
      {
        primary_id: primary.id,
        merge_ids: mergeIds,
        expected_updated_at: primary.updated_at,
        notes: notes || null,
      },
      {
        onSuccess: detail => {
          onDone?.(detail)
          onClose()
        },
      },
    )
  }

  return (
    <OmsDialog
      title="Merge outages"
      onClose={onClose}
      footer={
        <>
          <CancelButton onClick={onClose} />
          <PrimaryButton onClick={submit} disabled={!valid || mutation.isPending}>
            {mutation.isPending ? 'Merging…' : `Merge ${mergeIds.length} into #${primary.id}`}
          </PrimaryButton>
        </>
      }
    >
      <ErrorNote error={mutation.error} />
      <p className="text-gray-600">
        Roll up other active outages under primary #{primary.id}. Select the events
        to merge — no IDs to type.
      </p>
      {isLoading ? (
        <div className="text-xs text-gray-400 py-4 text-center">Loading active outages…</div>
      ) : candidates.length === 0 ? (
        <div className="text-xs text-gray-400 py-4 text-center">
          No other active outages to merge.
        </div>
      ) : (
        <div className="max-h-56 overflow-auto border border-gray-100 rounded-md divide-y divide-gray-50">
          {candidates.map(o => (
            <label
              key={o.id}
              className="flex items-center gap-2 px-2 py-1.5 text-sm hover:bg-gray-50 cursor-pointer"
            >
              <input
                type="checkbox"
                checked={selected.has(o.id)}
                onChange={() => toggle(o.id)}
              />
              <span className="font-mono text-xs text-gray-500">#{o.id}</span>
              <span className="text-gray-700">
                {deviceLabel(o.scope, o.device_id, o.unresolved_meter_number)}
              </span>
              <span className="ml-auto text-xs text-gray-400">
                {o.customers_affected ?? '--'} cust.
              </span>
            </label>
          ))}
        </div>
      )}
      <label className="block">
        <span className="text-xs text-gray-500">Notes (optional)</span>
        <textarea
          value={notes}
          onChange={e => setNotes(e.target.value)}
          rows={2}
          className="mt-1 w-full text-sm border border-gray-300 rounded-md px-2 py-1.5"
        />
      </label>
    </OmsDialog>
  )
}

// =========================================================================
// Split — break meters off into a new event
// =========================================================================

export function SplitOutageDialog({
  outage,
  onClose,
  onDone,
  presetMeterIds,
}: {
  outage: OutageDetail
  onClose: () => void
  onDone?: (detail: OutageDetail) => void
  presetMeterIds?: number[]
}) {
  const [selected, setSelected] = useState<Set<number>>(
    new Set(presetMeterIds ?? []),
  )
  const [notes, setNotes] = useState('')
  const mutation = useSplitOutage()
  // The event's own affected meters (paged; only rows with a resolved meter_id
  // can be split off — an unresolved meter has no meters.id to move).
  const { data, isLoading } = useAffectedMeters(outage.id, { limit: 100 })
  const meters = (data?.items ?? []).filter(m => m.meter_id !== null)

  const meterIds = [...selected]
  const valid = meterIds.length > 0

  function toggle(id: number) {
    setSelected(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  function submit() {
    if (!valid) return
    mutation.mutate(
      {
        id: outage.id,
        body: {
          meter_ids: meterIds,
          expected_updated_at: outage.updated_at,
          notes: notes || null,
        },
      },
      {
        onSuccess: detail => {
          onDone?.(detail)
          onClose()
        },
      },
    )
  }

  return (
    <OmsDialog
      title="Split outage"
      onClose={onClose}
      footer={
        <>
          <CancelButton onClick={onClose} />
          <PrimaryButton onClick={submit} disabled={!valid || mutation.isPending}>
            {mutation.isPending ? 'Splitting…' : `Split ${meterIds.length} meter(s) off`}
          </PrimaryButton>
        </>
      }
    >
      <ErrorNote error={mutation.error} />
      <p className="text-gray-600">
        Move a subset of affected meters off outage #{outage.id} into a new event.
        Select the meters — no IDs to type.
      </p>
      {isLoading ? (
        <div className="text-xs text-gray-400 py-4 text-center">Loading affected meters…</div>
      ) : meters.length === 0 ? (
        <div className="text-xs text-gray-400 py-4 text-center">
          No splittable meters on this outage.
        </div>
      ) : (
        <div className="max-h-56 overflow-auto border border-gray-100 rounded-md divide-y divide-gray-50">
          {meters.map(m => (
            <label
              key={m.meter_id as number}
              className="flex items-center gap-2 px-2 py-1.5 text-sm hover:bg-gray-50 cursor-pointer"
            >
              <input
                type="checkbox"
                checked={selected.has(m.meter_id as number)}
                onChange={() => toggle(m.meter_id as number)}
              />
              <span className="font-mono text-xs text-gray-700">
                {m.meter_number ?? `#${m.meter_id}`}
              </span>
              <span className="ml-auto text-xs text-gray-400 max-w-[200px] truncate" title={m.address ?? ''}>
                {m.address ?? ''}
              </span>
            </label>
          ))}
          {data?.next_cursor != null && (
            <div className="px-2 py-1.5 text-[10px] text-amber-600">
              Showing the first 100 meters — refine the outage before splitting a
              larger set.
            </div>
          )}
        </div>
      )}
      <label className="block">
        <span className="text-xs text-gray-500">Notes (optional)</span>
        <textarea
          value={notes}
          onChange={e => setNotes(e.target.value)}
          rows={2}
          className="mt-1 w-full text-sm border border-gray-300 rounded-md px-2 py-1.5"
        />
      </label>
    </OmsDialog>
  )
}

// =========================================================================
// Edit — operator ETR + priority (spec §4.4), via the optimistic PATCH
// =========================================================================

export function EditOutageDialog({
  outage,
  onClose,
  onDone,
}: {
  outage: OutageDetail
  onClose: () => void
  onDone?: (detail: OutageDetail) => void
}) {
  const [etr, setEtr] = useState(toDatetimeLocal(outage.estimated_restoration_at))
  // Seed ONLY a known enum value. A legacy free-text priority (pre-enum rows)
  // must not ride through an untouched Save into the strict write-enum (422);
  // it seeds as '' → PATCHes null unless the operator picks a real value.
  const [priority, setPriority] = useState<OutagePriority | ''>(
    OUTAGE_PRIORITIES.includes(outage.priority as OutagePriority)
      ? (outage.priority as OutagePriority)
      : '',
  )
  const mutation = usePatchOutage()

  function submit() {
    mutation.mutate(
      {
        id: outage.id,
        body: {
          expected_updated_at: outage.updated_at,
          // datetime-local is LOCAL wall time; toISOString normalizes to UTC Z.
          // Empty input clears the ETR (send null).
          estimated_restoration_at: etr ? new Date(etr).toISOString() : null,
          priority: priority || null,
        },
      },
      {
        onSuccess: detail => {
          onDone?.(detail)
          onClose()
        },
      },
    )
  }

  return (
    <OmsDialog
      title="Edit outage (ETR / priority)"
      onClose={onClose}
      footer={
        <>
          <CancelButton onClick={onClose} />
          <PrimaryButton onClick={submit} disabled={mutation.isPending}>
            {mutation.isPending ? 'Saving…' : 'Save'}
          </PrimaryButton>
        </>
      }
    >
      <ErrorNote error={mutation.error} />
      <label className="block">
        <span className="text-xs text-gray-500">Estimated restoration (ETR)</span>
        <div className="flex items-center gap-2 mt-1">
          <input
            type="datetime-local"
            value={etr}
            onChange={e => setEtr(e.target.value)}
            className="flex-1 text-sm border border-gray-300 rounded-md px-2 py-1.5"
          />
          <button
            type="button"
            onClick={() => setEtr('')}
            className="text-xs px-2 py-1 rounded border border-gray-300 text-gray-600 hover:bg-gray-50"
          >
            Clear
          </button>
        </div>
      </label>
      <label className="block">
        <span className="text-xs text-gray-500">Priority</span>
        <select
          value={priority}
          onChange={e => setPriority(e.target.value as OutagePriority | '')}
          className="mt-1 w-full text-sm border border-gray-300 rounded-md px-2 py-1.5"
        >
          <option value="">-- none --</option>
          {OUTAGE_PRIORITIES.map(pr => (
            <option key={pr} value={pr}>
              {PRIORITY_LABELS[pr]}
            </option>
          ))}
        </select>
      </label>
    </OmsDialog>
  )
}

// =========================================================================
// Cause — PowerTRX primary cause (post-event review, incl. closed outages)
// =========================================================================

/** Set or change an outage's PowerTRX cause. Allowed on active AND closed
 *  outages (the engine auto-closes most before review); the server derives the
 *  coarse cause_code. Optimistic on updated_at like every other action. */
export function CauseDialog({
  outage,
  onClose,
  onDone,
}: {
  outage: OutageDetail
  onClose: () => void
  onDone?: (detail: OutageDetail) => void
}) {
  const [cause, setCause] = useState<string | null>(outage.cause_detail ?? null)
  const [description, setDescription] = useState(outage.cause_description ?? '')
  const mutation = usePatchOutage()

  function submit() {
    mutation.mutate(
      {
        id: outage.id,
        body: {
          expected_updated_at: outage.updated_at,
          cause_detail: cause,
          cause_description: description.trim() || null,
        },
      },
      {
        onSuccess: detail => {
          onDone?.(detail)
          onClose()
        },
      },
    )
  }

  return (
    <OmsDialog
      title="Outage cause"
      onClose={onClose}
      footer={
        <>
          <CancelButton onClick={onClose} />
          <PrimaryButton onClick={submit} disabled={mutation.isPending || cause === null}>
            {mutation.isPending ? 'Saving…' : 'Save cause'}
          </PrimaryButton>
        </>
      }
    >
      <ErrorNote error={mutation.error} />
      <p className="text-gray-600">
        PowerTRX primary cause. It goes out with the PowerTRX export for EIA
        reliability reporting.
      </p>
      <label className="block">
        <span className="text-xs text-gray-500">Cause</span>
        <CausePicker value={cause} onChange={setCause} />
      </label>
      <label className="block">
        <span className="text-xs text-gray-500">Description (optional)</span>
        <input
          value={description}
          onChange={e => setDescription(e.target.value)}
          className="mt-1 w-full text-sm border border-gray-300 rounded-md px-2 py-1.5"
        />
      </label>
    </OmsDialog>
  )
}
